import {
  useGetAllBonusPoliciesQuery,
  type BonusPolicyItem,
} from '@/app/features/employee/emp-payroll';
import CustomTable from '@/components/CustomTable';
import StatusTag from '@/components/StatusTag';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters } from '@/hooks';
import { EyeIcon, PencilEdit02Icon } from '@/icons';
import { snakeToCamel } from '@/utils/string.utils';
import { Button, Space, Tooltip, type TableProps } from 'antd';
import DisburseBonus from './_components/BonusAddition/DisburseBonus';
import ManageBonus from './_components/BonusAddition/ManageBonus';
import {
  ACTIVE_STATUS_CONFIG,
  BONUS_PAY_BASIS_CONFIG,
} from './_components/shared/payroll.constants';
import { formatBDT, toAmount } from './_components/shared/payroll.helpers';

/** Drawers of this tab; the toolbar in `Filters` opens them too. */
export type BonusDrawerType = 'ADD' | 'UPDATE' | 'VIEW' | 'DISBURSE';

/** "৳5,000.00" for a flat policy, "50% of Gross" for a percentage one. */
const policyAmount = (policy: BonusPolicyItem) =>
  toAmount(policy.amount_percentage) > 0
    ? `${toAmount(policy.amount_percentage)}% of ${snakeToCamel(policy.amount_percentage_of || '')}`
    : formatBDT(policy.amount_flat, formatBDT(0));

/**
 * Bonus / Addition.
 *
 * Two separate jobs: a policy says who gets how much and how often a year;
 * disbursing pays one policy's bonus into a salary month. Policies are managed
 * from the table; "Disburse Bonus" in the toolbar pays one out.
 */
export default function BonusAddition() {
  const { page, limit, filters, keyword, handlePagination } = useAppFilters(
    FILTER_KEYS.PAYROLL_BONUS,
  );
  const [config, handleOpen, handleClose] = useAppDiscloser<
    BonusDrawerType,
    BonusPolicyItem
  >(DISCLOSER_KEYS.PAYROLL_BONUS);

  const { data, isLoading, isFetching } = useGetAllBonusPoliciesQuery({
    params: { page, limit, ...filters, search: keyword },
  });

  const columns: TableProps<BonusPolicyItem>['columns'] = [
    {
      title: 'Title',
      dataIndex: 'title',
      render: (val: string) => val || 'n/a',
    },
    {
      title: 'Amount',
      render: (_, policy) => policyAmount(policy),
    },
    {
      title: 'Per Fiscal Year',
      dataIndex: 'count_per_fiscal_year',
      align: 'center',
      render: (val?: number) => `${val || 0} time${val === 1 ? '' : 's'}`,
    },
    {
      title: 'Pay Basis',
      dataIndex: 'pay_basis',
      align: 'center',
      render: (val?: string | null) =>
        val ? <StatusTag value={val} config={BONUS_PAY_BASIS_CONFIG} /> : 'n/a',
    },
    {
      title: 'Employee Type',
      dataIndex: 'employee_types',
      render: (val: string[]) =>
        val?.map((type) => snakeToCamel(type)).join(', ') || 'n/a',
    },
    {
      title: 'Employees',
      dataIndex: 'user_ids',
      align: 'center',
      render: (val?: string[]) => val?.length ?? 0,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string) => (
        <StatusTag value={status} config={ACTIVE_STATUS_CONFIG} />
      ),
    },
    {
      title: 'Actions',
      align: 'end',
      render: (_, policy) => (
        <Space>
          <Tooltip title="View policy">
            <Button
              size="small"
              className="action-btn"
              icon={<EyeIcon size={20} />}
              onClick={() => handleOpen({ type: 'VIEW', data: policy })}
            />
          </Tooltip>
          <Tooltip title="Edit policy">
            <Button
              size="small"
              className="action-btn"
              icon={<PencilEdit02Icon size={20} />}
              onClick={() => handleOpen({ type: 'UPDATE', data: policy })}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const isPolicyDrawer =
    config.type === 'ADD' || config.type === 'UPDATE' || config.type === 'VIEW';

  return (
    <div>
      <CustomTable<BonusPolicyItem>
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={data?.data?.data || []}
        onPaginate={handlePagination}
        tableRootClassName="!bg-transparent !border-none !p-0"
        customTitle="Bonus Policies"
        loading={isLoading || isFetching}
        total={data?.data?.meta?.total || 0}
      />

      <ManageBonus
        open={config.open && isPolicyDrawer}
        mode={
          config.type === 'ADD'
            ? 'ADD'
            : config.type === 'VIEW'
              ? 'VIEW'
              : 'UPDATE'
        }
        id={config.type === 'ADD' ? undefined : config.data?.id}
        handleClose={handleClose}
      />

      <DisburseBonus
        open={config.open && config.type === 'DISBURSE'}
        handleClose={handleClose}
      />
    </div>
  );
}
